/**
 * Every API read and write the screens use, as TanStack Query hooks (decision 4.4).
 *
 * Queries cache by key; mutations invalidate exactly the keys they change, so the rail, the hero
 * and the dialogs stay consistent without passing data around. Job progress polls every 3 seconds
 * only while something is queued or running (decision 3.2).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { isFinished } from "@/api/jobs";

export type Study = Schemas["Study"];
export type StudySummary = Schemas["StudySummary"];
export type JobView = Schemas["JobView"];
export type RunRecord = Schemas["RunRecord"];
export type Audience = Schemas["Audience"];
export type Me = Schemas["Me"];
export type Dashboard = Schemas["Dashboard"];
export type ThemeSet = Schemas["ThemeSet"];

export const keys = {
  me: ["me"] as const,
  branding: ["branding"] as const,
  studies: ["studies"] as const,
  study: (id: string) => ["study", id] as const,
  runs: (id: string) => ["study", id, "runs"] as const,
  run: (id: string, runId: string) => ["study", id, "run", runId] as const,
  jobs: (id: string) => ["study", id, "jobs"] as const,
  estimate: (id: string, variant: string | null) => ["study", id, "estimate", variant] as const,
  audiences: ["audiences"] as const,
};

/** Unwraps an openapi-fetch result, turning API errors into thrown Errors with the server's message. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined) return result.data;
  throw new ApiError(result.response.status, describe(result.error));
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function describe(error: unknown): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) return String((detail as { message: unknown }).message);
  return "something went wrong";
}

export function useMe(enabled = true) {
  return useQuery({ queryKey: keys.me, enabled, queryFn: async () => unwrap(await api.GET("/api/v1/me")) });
}

export function useStudies() {
  return useQuery({ queryKey: keys.studies, queryFn: async () => unwrap(await api.GET("/api/v1/studies")) });
}

export function useStudy(studyId: string | undefined) {
  return useQuery({
    queryKey: keys.study(studyId ?? ""),
    enabled: Boolean(studyId),
    queryFn: async () => unwrap(await api.GET("/api/v1/studies/{study_id}", { params: { path: { study_id: studyId! } } })),
  });
}

export function useRuns(studyId: string | undefined) {
  return useQuery({
    queryKey: keys.runs(studyId ?? ""),
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/studies/{study_id}/runs", { params: { path: { study_id: studyId! } } })),
  });
}

/** Everything the Result page draws, worked out by the API (decision 5.1). */
export function useDashboard(studyId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: [...keys.run(studyId ?? "", runId ?? ""), "dashboard"] as const,
    enabled: Boolean(studyId && runId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/runs/{run_id}/dashboard", {
          params: { path: { study_id: studyId!, run_id: runId! } },
        }),
      ),
  });
}

export function useRun(studyId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: keys.run(studyId ?? "", runId ?? ""),
    enabled: Boolean(studyId && runId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/runs/{run_id}", {
          params: { path: { study_id: studyId!, run_id: runId! } },
        }),
      ),
  });
}

/** A study's jobs, polled every 3s while any is unfinished; runs and the study refresh as one ends. */
export function useJobs(studyId: string | undefined) {
  const client = useQueryClient();
  return useQuery({
    queryKey: keys.jobs(studyId ?? ""),
    enabled: Boolean(studyId),
    queryFn: async () => {
      const jobs = unwrap(
        await api.GET("/api/v1/studies/{study_id}/jobs", { params: { path: { study_id: studyId! } } }),
      );
      const previous = client.getQueryData<JobView[]>(keys.jobs(studyId!)) ?? [];
      const justFinished = jobs.some(
        (j) => isFinished(j) && previous.some((p) => p.job_id === j.job_id && !isFinished(p)),
      );
      if (justFinished) {
        void client.invalidateQueries({ queryKey: keys.runs(studyId!) });
        void client.invalidateQueries({ queryKey: keys.me });
        // Suggestions, tests and adoptions all finish here, so the Goal seek panel and the study
        // itself (its working version, its suggestion sets) have to be re-read. `exact` matters:
        // keys.study is a prefix of this very query's key, and invalidating it loosely from inside
        // the query sends it round in circles - which it did, at 10 polls a second.
        void client.invalidateQueries({ queryKey: ["study", studyId, "goal-seek"] });
        void client.invalidateQueries({ queryKey: keys.study(studyId!), exact: true });
      }
      return jobs;
    },
    refetchInterval: (query) => ((query.state.data ?? []).some((j) => !isFinished(j)) ? 3000 : false),
  });
}

export function useAudiences() {
  return useQuery({ queryKey: keys.audiences, queryFn: async () => unwrap(await api.GET("/api/v1/audiences")) });
}

/** Which published populations this deployment can draw personas from (decision 6.2). */
export function useGroundings() {
  return useQuery({
    queryKey: [...keys.audiences, "groundings"] as const,
    staleTime: Infinity, // they change with a deploy, not while someone is filling in a form
    queryFn: async () => unwrap(await api.GET("/api/v1/audiences/groundings")),
  });
}

/** What building an audience of this size would cost, before anyone commits to it (decision 6.3). */
export function useAudienceCost(personaCount: number, mode: "mock" | "live") {
  return useQuery({
    queryKey: [...keys.audiences, "cost", personaCount, mode] as const,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/audiences/cost", { params: { query: { persona_count: personaCount, mode } } })),
  });
}

/** Follows one audience while it is being built; stops polling once it is ready or has failed. */
export function useAudience(audienceId: string | null) {
  return useQuery({
    queryKey: [...keys.audiences, audienceId] as const,
    enabled: Boolean(audienceId),
    refetchInterval: (query) => (query.state.data?.status === "building" ? 3000 : false),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/audiences/{audience_id}", { params: { path: { audience_id: audienceId! } } })),
  });
}

export function useBuildAudience() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["NewAudience"]) => unwrap(await api.POST("/api/v1/audiences", { body })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.audiences });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useEstimate(studyId: string | undefined, variantId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: keys.estimate(studyId ?? "", variantId),
    enabled: enabled && Boolean(studyId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/estimate", {
          params: { path: { study_id: studyId! }, query: { kind: "ask", variant_id: variantId } },
        }),
      ),
  });
}

/**
 * What a study that does not exist yet would cost.
 *
 * The Ask dialog offers Live before anything has been created, so until there is a study there is
 * nothing for `useEstimate` to ask about - and a paid action has to show its price before it is
 * confirmed, not after. Same endpoint family, same call plan, same answer.
 */
export function useDraftEstimate(mode: "mock" | "live", audienceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["estimate", "draft", mode, audienceId] as const,
    enabled,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/estimate", {
          params: { query: { mode, audience_id: audienceId } },
        }),
      ),
  });
}

/** The published gallery. Public: no sign-in, and no organisation behind it. */
export function useShowcase() {
  return useQuery({
    queryKey: ["showcase"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/showcase")),
  });
}

/** One frozen result. The same shape the Result page draws, recorded rather than live. */
export function usePublishedExample(slug: string | undefined) {
  return useQuery({
    queryKey: ["showcase", slug ?? ""] as const,
    enabled: Boolean(slug),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/showcase/{slug}", { params: { path: { slug: slug! } } })),
  });
}

export function usePublishExample(studyId: string | undefined, runId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["PublishRequest"]) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/runs/{run_id}/publish", {
          params: { path: { study_id: studyId!, run_id: runId! } },
          body,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ["showcase"] }),
  });
}

export function useCreateStudy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["CreateStudy"]) => unwrap(await api.POST("/api/v1/studies", { body })),
    onSuccess: (study) => {
      client.setQueryData(keys.study(study.study_id), study);
      void client.invalidateQueries({ queryKey: keys.studies });
    },
  });
}

export function useUpdateStudy(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["UpdateStudy"]) =>
      unwrap(await api.PATCH("/api/v1/studies/{study_id}", { params: { path: { study_id: studyId } }, body })),
    onSuccess: (study) => {
      client.setQueryData(keys.study(studyId), study);
      void client.invalidateQueries({ queryKey: keys.studies });
    },
  });
}

export function useAddVariant(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["NewVariant"]) =>
      unwrap(await api.POST("/api/v1/studies/{study_id}/variants", { params: { path: { study_id: studyId } }, body })),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.study(studyId) }),
  });
}

export function useSubmitJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ studyId, variantId }: { studyId: string; variantId: string | null }) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/jobs", {
          params: { path: { study_id: studyId } },
          body: { kind: "ask", variant_id: variantId },
        }),
      ),
    onSuccess: (job) => {
      void client.invalidateQueries({ queryKey: keys.jobs(job.study_id) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useCancelJob(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) =>
      unwrap(await api.POST("/api/v1/jobs/{job_id}/cancel", { params: { path: { job_id: jobId } } })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs(studyId) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

// --- What if (decisions 6.8-6.10) ----------------------------------------------------------------

export type Diff = Schemas["Diff"];
export type Comparison = Schemas["Comparison"];
export type QuickLook = { indicative: boolean; personas: number; rounds: number; model: string;
  metrics: { n: number; values: Record<string, number>; category_counts: Record<string, number> };
  drivers: { drivers: { sentence: string; positive: number; neutral: number; negative: number; total: number }[] };
  posts: { persona_id: string; stance: number; post: string; reasons: string[] }[] };

/** What an edit changes, word by word. Free: no model is called. */
export function useDiff(studyId: string | undefined, text: string, baseVariantId: string | null) {
  return useQuery({
    queryKey: ["study", studyId, "diff", baseVariantId, text] as const,
    enabled: Boolean(studyId) && text.trim().length > 0,
    staleTime: Infinity,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/what-if/diff", {
          params: { path: { study_id: studyId! }, query: { text, base_variant_id: baseVariantId } },
        }),
      ),
  });
}

/** What a quick look would cost, and what it is - shown before anyone spends anything. */
export function useQuickLookCost(studyId: string | undefined) {
  return useQuery({
    queryKey: ["study", studyId, "quick-look-cost"] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/what-if/preview/cost", {
          params: { path: { study_id: studyId! } },
        }),
      ),
  });
}

export function useQuickLook(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { text: string; base_variant_id?: string | null }) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/what-if/preview", {
          params: { path: { study_id: studyId } },
          body: { text: body.text, base_variant_id: body.base_variant_id ?? null },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs(studyId) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Two finished runs, paired, with the verdict rule of decision 6.9 already applied. */
export function useComparison(studyId: string | undefined, a: string | null, b: string | null) {
  return useQuery({
    queryKey: ["study", studyId, "compare", a, b] as const,
    enabled: Boolean(studyId && a && b && a !== b),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/what-if/compare", {
          params: { path: { study_id: studyId! }, query: { a: a!, b: b! } },
        }),
      ),
  });
}

// --- Goal seek (decisions 6.11-6.13) -------------------------------------------------------------

export type GoalSeek = Schemas["GoalSeek"];
export type Objective = Schemas["Objective"];
export type ConcernHeadroom = Schemas["ConcernHeadroom"];
export type CandidateView = Schemas["CandidateView"];
export type Triage = { indicative: boolean; looks: { candidate_id: string; personas: number; rounds: number;
  metrics: { n: number; values: Record<string, number>; category_counts: Record<string, number> } }[] };

/** The target, where a result stands against it, the ceilings, and the suggestions so far. */
export function useGoalSeek(studyId: string | undefined, runId: string | null) {
  return useQuery({
    queryKey: ["study", studyId, "goal-seek", runId] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/goal-seek", {
          params: { path: { study_id: studyId! }, query: { run_id: runId } },
        }),
      ),
  });
}

export function useGoalSeekPrices(studyId: string | undefined, candidates: number) {
  return useQuery({
    queryKey: ["study", studyId, "goal-seek-prices", candidates] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/studies/{study_id}/goal-seek/prices", {
          params: { path: { study_id: studyId! }, query: { candidates } },
        }),
      ),
  });
}

export function useSetObjective(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (objective: Objective) =>
      unwrap(
        await api.PUT("/api/v1/studies/{study_id}/goal-seek/objective", {
          params: { path: { study_id: studyId } },
          body: objective,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ["study", studyId, "goal-seek"] }),
  });
}

/** Ask for candidate edits. One model call plus a claims audit each; the price is shown first. */
export function useSuggest(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/goal-seek/suggest", {
          params: { path: { study_id: studyId } },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs(studyId) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** One quick look at every candidate (6.13), or the real thing on the main and holdout communities. */
export function useCandidateJob(studyId: string, what: "triage" | "test") {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { batch_id: string; selections: Record<string, string> }) =>
      unwrap(
        what === "triage"
          ? await api.POST("/api/v1/studies/{study_id}/goal-seek/triage", {
              params: { path: { study_id: studyId } },
              body,
            })
          : await api.POST("/api/v1/studies/{study_id}/goal-seek/test", {
              params: { path: { study_id: studyId } },
              body,
            }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs(studyId) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useAdopt(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/goal-seek/adopt", {
          params: { path: { study_id: studyId } },
          body: { variant_id: variantId },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.study(studyId) });
      void client.invalidateQueries({ queryKey: ["study", studyId, "goal-seek"] });
    },
  });
}

// --- commitments, settings and history (decisions 6.5, 6.6) --------------------------------------

export type CommitmentProposal = Schemas["CommitmentProposalView"];
export type StudySettings = Schemas["SettingsView"];
export type History = Schemas["History"];

export function useCommitments(studyId: string | undefined) {
  return useQuery({
    queryKey: ["study", studyId, "commitments"] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/studies/{study_id}/commitments", { params: { path: { study_id: studyId! } } })),
  });
}

/** Ask what would have to change in the real world to address one concern. */
export function useProposeCommitment(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { run_id: string; theme_id: string }) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/commitments", {
          params: { path: { study_id: studyId } },
          body,
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs(studyId) });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useAdoptCommitment(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { proposal_id: string; option: number }) =>
      unwrap(
        await api.POST("/api/v1/studies/{study_id}/commitments/adopt", {
          params: { path: { study_id: studyId } },
          body,
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["study", studyId, "commitments"] });
      void client.invalidateQueries({ queryKey: ["study", studyId, "history"] });
      void client.invalidateQueries({ queryKey: keys.study(studyId), exact: true });
    },
  });
}

export function useStudySettings(studyId: string | undefined) {
  return useQuery({
    queryKey: ["study", studyId, "settings"] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/studies/{study_id}/settings", { params: { path: { study_id: studyId! } } })),
  });
}

export function useUpdateSettings(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { personas?: number; rounds?: number }) =>
      unwrap(
        await api.PATCH("/api/v1/studies/{study_id}/settings", {
          params: { path: { study_id: studyId } },
          body,
        }),
      ),
    onSuccess: (settings) => client.setQueryData(["study", studyId, "settings"], settings),
  });
}

/** The version tree: where each version came from, what changed, and how it did. */
export function useHistory(studyId: string | undefined) {
  return useQuery({
    queryKey: ["study", studyId, "history"] as const,
    enabled: Boolean(studyId),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/studies/{study_id}/history", { params: { path: { study_id: studyId! } } })),
  });
}

export function useSetWorkingVersion(studyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) =>
      unwrap(
        await api.PUT("/api/v1/studies/{study_id}/working-version", {
          params: { path: { study_id: studyId } },
          body: { variant_id: variantId },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["study", studyId, "history"] });
      void client.invalidateQueries({ queryKey: keys.study(studyId), exact: true });
    },
  });
}

// --- billing (Phase 7) ---------------------------------------------------------------------------

export type Billing = Schemas["BillingView"];

export function useBilling() {
  return useQuery({
    queryKey: ["billing"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/billing")),
  });
}

/** Sends the person to Stripe. What they bought comes back as a webhook, not as a redirect. */
export function useCheckout() {
  return useMutation({
    mutationFn: async (body: { plan?: string | null; topup?: boolean }) =>
      unwrap(await api.POST("/api/v1/billing/checkout", { body: { plan: body.plan ?? null, topup: body.topup ?? false } })),
    onSuccess: (redirect) => window.location.assign(redirect.url),
  });
}

/** Cards, plan changes and cancellation happen in Stripe's own portal. */
export function usePortal() {
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/billing/portal")),
    onSuccess: (redirect) => window.location.assign(redirect.url),
  });
}

/** Credit in and credit out, from our own records rather than Stripe's (decision 7.11). */
export function useBillingHistory() {
  return useQuery({
    queryKey: ["billing", "history"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/billing/history")),
  });
}

/** The business's own view. These 404 for anyone not named in APP_ADMIN_EMAILS. */
export function useAdminOverview(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "overview"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/overview")),
    enabled,
  });
}

export function useAdminOrgs(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "orgs"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/orgs")),
    enabled,
  });
}

export function useGrantCredit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { orgId: string; usd: number; reason: string }) =>
      unwrap(await api.POST("/api/v1/admin/orgs/{org_id}/credit", {
        params: { path: { org_id: body.orgId } },
        body: { usd: body.usd, reason: body.reason },
      })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

/** The kill switch: what it is doing, and what the last 24 hours cost us. */
export function useKillSwitch(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "switch"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/switch")),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useSetKillSwitch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { paused: boolean; reason?: string }) =>
      unwrap(await api.POST("/api/v1/admin/switch", {
        body: { paused: body.paused, reason: body.reason ?? "" },
      })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

/** What deleting an organisation would destroy, and what it would leave. Changes nothing. */
export function useDeletionPreview(orgId: string | null) {
  return useQuery({
    queryKey: ["admin", "deletion", orgId] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/orgs/{org_id}/deletion", {
      params: { path: { org_id: orgId! } },
    })),
    enabled: !!orgId,
  });
}

export function useDeleteOrg() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { orgId: string; confirmName: string; deleteUserIds: string[] }) =>
      unwrap(await api.POST("/api/v1/admin/orgs/{org_id}/deletion", {
        params: { path: { org_id: body.orgId } },
        body: { confirm_name: body.confirmName, delete_user_ids: body.deleteUserIds },
      })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

/** Agreeing to the terms in force. Only the current version can be accepted (decision 8.14). */
export function useAcceptTerms() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (version: string) =>
      unwrap(await api.POST("/api/v1/me/accept-terms", { body: { version } })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Example messages to start from. Public, and the same for everyone, so it is cached hard. */
export function useExamples() {
  return useQuery({
    queryKey: ["examples"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/examples")),
    staleTime: Infinity,
  });
}
